import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, Users, BarChart3, Send, Download, LogOut, Eye, Megaphone, Mail, RefreshCw, FileText, Mic } from "lucide-react";
import {
  adminListSubscribers,
  adminPatchStats,
  adminSendBlast,
  fetchStats,
  adminCsvUrl,
} from "../lib/api";
import ArticlesPanel from "../components/ArticlesPanel";
import EpisodesPanel from "../components/EpisodesPanel";

const TOKEN_KEY = "uob_admin_token";

function Login({ onAuth }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      await adminListSubscribers(token.trim());
      localStorage.setItem(TOKEN_KEY, token.trim());
      onAuth(token.trim());
      toast.success("Welcome back, coach.");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Invalid token." : "Couldn't verify.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid="admin-login">
      <div className="sticker-card w-full max-w-md p-8">
        <div className="flex items-center gap-3 mb-5">
          <Lock size={18} className="text-[var(--rust)]" />
          <span className="text-kicker">Locker Room Access</span>
        </div>
        <h1 className="text-headline text-4xl mb-2">Admin</h1>
        <p className="text-dek mb-6">Enter your admin token. Get it from <code className="font-mono-ed text-[var(--rust)]">/app/backend/.env</code>.</p>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="password"
            className="field"
            placeholder="admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-token-input"
            autoFocus
          />
          <button type="submit" className="btn-rust w-full justify-center" disabled={loading} data-testid="admin-login-submit">
            {loading ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function SubscribersPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListSubscribers(token);
      setData(d);
    } catch { toast.error("Couldn't load subscribers."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const downloadCsv = async () => {
    try {
      const res = await fetch(adminCsvUrl, { headers: { "X-Admin-Token": token } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "unfiltered-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  return (
    <div data-testid="admin-subscribers-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-kicker">Newsletter</div>
          <h2 className="text-headline text-3xl md:text-4xl">Subscribers</h2>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="btn-ghost text-xs" data-testid="subs-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={downloadCsv} className="btn-rust" data-testid="subs-download-csv">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="sticker-card p-5">
          <div className="text-kicker">Total</div>
          <div className="stat-number text-4xl md:text-5xl mt-1">{data?.count ?? "—"}</div>
        </div>
        <div className="sticker-card p-5">
          <div className="text-kicker">Active</div>
          <div className="stat-number text-4xl md:text-5xl mt-1 text-[var(--rust)]">{data?.active ?? "—"}</div>
        </div>
        <div className="sticker-card p-5">
          <div className="text-kicker">Unsubscribed</div>
          <div className="stat-number text-4xl md:text-5xl mt-1 text-[var(--muted)]">{data?.unsubscribed ?? "—"}</div>
        </div>
      </div>

      <div className="border-2 border-[var(--ink)] overflow-hidden" data-testid="subs-table">
        <div className="grid grid-cols-12 bg-[var(--ink)] text-[var(--paper)] font-display uppercase tracking-widest text-xs px-4 py-3">
          <div className="col-span-5">Email</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-2">Status</div>
        </div>
        {loading ? (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">Loading…</div>
        ) : data?.subscribers?.length ? (
          <div className="divide-y divide-[var(--line)]">
            {data.subscribers.map((s) => (
              <div key={s.id} className="grid grid-cols-12 px-4 py-3 text-sm items-center hover:bg-[var(--paper-deep)]/40">
                <div className="col-span-5 truncate">{s.email}</div>
                <div className="col-span-3 truncate text-[var(--muted)]">{s.name || "—"}</div>
                <div className="col-span-2 truncate text-[var(--muted)] font-mono-ed text-xs">{s.source || "—"}</div>
                <div className="col-span-2">
                  <span className={`chip text-[0.65rem] ${s.unsubscribed ? "" : "chip-active"}`}>
                    {s.unsubscribed ? "Unsubscribed" : "Active"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">No subscribers yet.</div>
        )}
      </div>
    </div>
  );
}

function StatsPanel({ token }) {
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats().then((s) => { setStats(s); setForm(s); });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) || 0 });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const patch = {};
      ["tiktok", "instagram", "facebook", "twitch", "youtube", "spotify", "total_downloads", "avg_monthly_listeners"].forEach((k) => {
        if (form[k] !== stats?.[k]) patch[k] = form[k];
      });
      if (Object.keys(patch).length === 0) { toast.info("No changes."); setLoading(false); return; }
      const res = await adminPatchStats(token, patch);
      setStats(res); setForm(res);
      toast.success("Stats updated.");
    } catch { toast.error("Couldn't update."); }
    finally { setLoading(false); }
  };

  if (!stats) return <div className="py-10 text-center font-mono-ed uppercase text-xs tracking-widest text-[var(--muted)]">Loading stats…</div>;

  const FIELDS = [
    { k: "tiktok", label: "TikTok Followers" },
    { k: "instagram", label: "Instagram Followers" },
    { k: "facebook", label: "Facebook Followers" },
    { k: "twitch", label: "Twitch Followers" },
    { k: "youtube", label: "YouTube Subscribers" },
    { k: "spotify", label: "Spotify Followers" },
    { k: "avg_monthly_listeners", label: "Avg Monthly Listeners" },
    { k: "total_downloads", label: "Total Downloads (lifetime)" },
  ];

  return (
    <div data-testid="admin-stats-panel">
      <div className="mb-6">
        <div className="text-kicker">Metric Dashboard</div>
        <h2 className="text-headline text-3xl md:text-4xl">Update Stats</h2>
        <p className="text-dek mt-1">These numbers flow into the Media Kit + PDF immediately.</p>
      </div>
      <form onSubmit={save} className="sticker-card p-6 md:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <label className="text-kicker block mb-1">{f.label}</label>
              <input
                type="number"
                className="field"
                value={form[f.k] ?? 0}
                onChange={set(f.k)}
                data-testid={`stats-input-${f.k}`}
                min="0"
              />
            </div>
          ))}
        </div>
        <button type="submit" className="btn-rust" disabled={loading} data-testid="stats-save-button">
          {loading ? "Saving…" : "Save Stats"}
        </button>
      </form>
    </div>
  );
}

function BlastPanel({ token }) {
  const [form, setForm] = useState({
    subject: "",
    body_html: "",
    cta_label: "",
    cta_url: "",
  });
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const preview = async () => {
    const to = prompt("Send preview to which email?", "unfilteredonball@gmail.com");
    if (!to) return;
    setPreviewing(true);
    try {
      const res = await adminSendBlast(token, { ...form, test_to: to });
      setLastResult(res);
      if (res?.result?.ok) toast.success(`Preview sent to ${to}`);
      else toast.error(`Preview failed: ${res?.result?.error || "unknown"}`);
    } catch (err) {
      toast.error("Preview request failed.");
    } finally { setPreviewing(false); }
  };

  const blast = async () => {
    if (!form.subject || !form.body_html) {
      toast.error("Subject and body are required."); return;
    }
    if (!window.confirm("Send to ALL active subscribers? This can't be undone.")) return;
    setSending(true);
    try {
      const payload = { ...form };
      delete payload.test_to;
      const res = await adminSendBlast(token, payload);
      setLastResult(res);
      toast.success(`Broadcast done · ${res.sent}/${res.total} sent.`);
    } catch { toast.error("Broadcast failed."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-blast-panel">
      <div className="mb-6">
        <div className="text-kicker">Newsletter</div>
        <h2 className="text-headline text-3xl md:text-4xl">Compose a Blast</h2>
        <p className="text-dek mt-1">Write once, drop to everyone. Preview before sending.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <form className="lg:col-span-3 sticker-card p-6 md:p-8 space-y-5" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label className="text-kicker block mb-1">Subject</label>
            <input className="field" value={form.subject} onChange={set("subject")}
                   placeholder="Monday Drop · Ep 41 — The Chaos Recap"
                   data-testid="blast-subject-input" />
          </div>
          <div>
            <label className="text-kicker block mb-1">Body (HTML allowed)</label>
            <textarea className="field min-h-[220px] resize-y font-mono-ed text-sm"
                      value={form.body_html} onChange={set("body_html")}
                      placeholder="<p>What went down this weekend…</p>"
                      data-testid="blast-body-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-kicker block mb-1">CTA Label (optional)</label>
              <input className="field" value={form.cta_label} onChange={set("cta_label")}
                     placeholder="Play Episode 41"
                     data-testid="blast-cta-label-input" />
            </div>
            <div>
              <label className="text-kicker block mb-1">CTA URL (optional)</label>
              <input className="field" type="url" value={form.cta_url} onChange={set("cta_url")}
                     placeholder="https://open.spotify.com/…"
                     data-testid="blast-cta-url-input" />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button type="button" onClick={preview} disabled={previewing || sending}
                    className="btn-ghost" data-testid="blast-preview-button">
              <Eye size={14} /> {previewing ? "Sending preview…" : "Send Preview"}
            </button>
            <button type="button" onClick={blast} disabled={sending || previewing}
                    className="btn-rust" data-testid="blast-send-button">
              <Megaphone size={14} /> {sending ? "Broadcasting…" : "Blast to All"}
            </button>
          </div>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="border-2 border-[var(--ink)] bg-[var(--paper)] p-5">
            <div className="text-kicker flex items-center gap-2"><Mail size={14} /> Preview</div>
            <div className="mt-4 border border-[var(--line)] p-4 bg-[var(--paper-deep)]/40">
              <div className="font-display uppercase tracking-widest text-xs text-[var(--rust)]">The Drop</div>
              <div className="font-display uppercase text-xl mt-1 leading-tight">{form.subject || "Subject preview…"}</div>
              <div className="text-sm mt-3 text-[var(--ink-soft)]"
                   dangerouslySetInnerHTML={{ __html: form.body_html || "<em style='color:#6B5E52'>Body preview…</em>" }} />
              {form.cta_label && form.cta_url && (
                <div className="mt-4">
                  <span className="btn-rust text-[0.7rem] py-2 px-3">{form.cta_label}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-[var(--muted)] mt-3 font-mono-ed uppercase tracking-widest">
              The real email includes the masthead, footer, and one-click unsubscribe.
            </p>
          </div>

          {lastResult && (
            <div className="border-2 border-[var(--ink)] p-4 bg-[var(--paper)]" data-testid="blast-last-result">
              <div className="text-kicker">Last Send</div>
              <pre className="text-xs mt-2 font-mono-ed whitespace-pre-wrap break-all">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("subscribers");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    toast.success("Locked out. Come back when you're ready.");
  };

  if (!token) return <Login onAuth={setToken} />;

  const tabs = [
    { key: "subscribers", label: "Subscribers", icon: Users },
    { key: "episodes", label: "Episodes", icon: Mic },
    { key: "articles", label: "Articles", icon: FileText },
    { key: "stats", label: "Stats", icon: BarChart3 },
    { key: "blast", label: "Blast", icon: Send },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-8 border-b-2 border-[var(--ink)]">
        <div>
          <div className="text-kicker text-[var(--rust)]">Locker Room</div>
          <h1 className="text-headline text-4xl md:text-5xl">Admin</h1>
        </div>
        <button onClick={logout} className="btn-ghost text-xs" data-testid="admin-logout">
          <LogOut size={14} /> Log out
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b-2 border-[var(--ink)] overflow-x-auto" data-testid="admin-tabs">
        {tabs.map((t) => {
          const Ic = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 px-5 py-3 font-display uppercase tracking-widest text-sm border-b-4 -mb-[2px] transition ${active ? "border-[var(--rust)] text-[var(--rust)]" : "border-transparent text-[var(--ink)] hover:text-[var(--rust)]"}`}
                    data-testid={`admin-tab-${t.key}`}>
              <Ic size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "subscribers" && <SubscribersPanel token={token} />}
      {tab === "episodes" && <EpisodesPanel token={token} />}
      {tab === "articles" && <ArticlesPanel token={token} />}
      {tab === "stats" && <StatsPanel token={token} />}
      {tab === "blast" && <BlastPanel token={token} />}
    </div>
  );
}
